import { useCallback, useMemo } from "react"

import { getAvailableIndexesForSession } from "@/features/navigator/utils/available-indexes"
import { useCurrentSessionProject } from "@/features/projects/hooks/use-current-session-project"
import { useAIExplorerSettings } from "@/hooks/use-module-settings"
import { useProject } from "@/hooks/use-projects"
import { useRouteOverrides } from "@/hooks/use-route-overrides"
import { SettingsService } from "@/services/settings-service"

import { useNavigatorLibrariesSync } from "./use-navigator-libraries-sync"

type LibrarySelectionItem = {
  id: string
  name: string
  description: string
  lastUsedAt?: number
}

type LibrariesSelection = {
  hasLibraryTool: boolean
  availableLibraries: LibrarySelectionItem[]
  activeLibraryIds: string[]
  setActiveLibraryIds: (ids: string[]) => void
  isLoading: boolean
  showCategoryPicker: boolean
  restrictedLibraryId?: string
}

export function useLibrariesSelection(sessionType: string): LibrariesSelection {
  const { settings } = useAIExplorerSettings(sessionType)
  const overrides = useRouteOverrides()

  // If the current session is linked to a project with pinned default libraries,
  // pass that pin through to the sync hook so it seeds the navigator from the
  // pin instead of the user's last-used libraries.
  const currentProjectId = useCurrentSessionProject()
  const { data: currentProject } = useProject(currentProjectId ?? undefined, sessionType)
  const projectPin =
    currentProject && Array.isArray(currentProject.defaultEnabledIndexIds)
      ? currentProject.defaultEnabledIndexIds
      : null

  const { enabledLibraries, toggleLibraryChecked, isLoading } = useNavigatorLibrariesSync(
    sessionType,
    projectPin
  )

  const hasLibraryTool =
    SettingsService.getInstance().settings.enabled_tools?.includes("library_search") ?? false

  const availableLibraries = useMemo(() => {
    const userLibrarySettings = settings?.enabled_libraries || {}

    let libraries = getAvailableIndexesForSession(sessionType).map((index) => ({
      id: index.id,
      name: index.display_name,
      description: index.description,
      lastUsedAt: (userLibrarySettings[index.id] as { lastUsedAt?: number } | undefined)
        ?.lastUsedAt,
    }))

    if (overrides.restrictToLibraryId) {
      const restrictedLibraryId = overrides.restrictToLibraryId as string
      libraries = libraries.filter((lib) => lib.id === restrictedLibraryId)
    } else if (overrides.restrictToLibraryIds) {
      const allowed = overrides.restrictToLibraryIds as string[]
      libraries = libraries.filter((lib) => allowed.includes(lib.id))
    }

    return libraries
  }, [overrides, sessionType, settings])

  const availableLibraryIds = useMemo(
    () => availableLibraries.map((lib) => lib.id),
    [availableLibraries]
  )

  const activeLibraryIds = useMemo(
    () =>
      Object.entries(enabledLibraries)
        .filter(([id, lib]) => lib.checked && availableLibraryIds.includes(id))
        .map(([id]) => id),
    [enabledLibraries, availableLibraryIds]
  )

  const setActiveLibraryIds = useCallback(
    (ids: string[]) => {
      availableLibraries.forEach((lib) => {
        const shouldBeActive = ids.includes(lib.id)
        const isCurrentlyActive = activeLibraryIds.includes(lib.id)

        if (shouldBeActive !== isCurrentlyActive) {
          toggleLibraryChecked(lib.id)
        }
      })
    },
    [activeLibraryIds, availableLibraries, toggleLibraryChecked]
  )

  return {
    hasLibraryTool,
    availableLibraries,
    activeLibraryIds,
    setActiveLibraryIds,
    isLoading,
    showCategoryPicker: Boolean(overrides.showCategoryPicker && overrides.restrictToLibraryId),
    restrictedLibraryId: overrides.restrictToLibraryId as string | undefined,
  }
}
